import React, { useEffect, useState } from 'react';
import {
  Button,
  Frame,
  Icon,
  SageClassnames,
  useSageBreakpoint,
  Grid,
} from '@kajabi/sage-react';
import { withOptimizely, WithOptimizelyProps } from '@optimizely/react-sdk';
import uniqBy from 'lodash/uniqBy';
import BusinessPreviewSlideTab from '../components/BusinessPreviewSlideTab';
import BusinessPreviewSlideContent from '../components/BusinessPreviewSlideContent';
import { useUnsplash } from '../../../hooks/useUnsplash';
import { buildSignupUrl } from '../../../services/utils_service';
import {
  CHECKOUT_ID,
  COACHING_PROGRAM_ID,
  COMMUNITY_ID,
  COURSE_ID,
  LANDING_PAGE_ID,
  OPTIMIZELY_CONSTANTS,
  PODCAST_ID,
  RUDDERSTACK_CONSTANTS,
  SALES_EMAIL_ID,
  rudderstackTrackEvent,
} from '../../../utils/constants';

interface PropsType extends WithOptimizelyProps {
  onStepChange: () => void;
  contentTab: string;
  optimizely: any;
  userData: {
    optimizely_id: string;
    email: string;
    full_name: string | null;
    signup_method: string | null;
    business_topic: string;
    plan_to_sell: string;
  };
}

interface UnsplashImageObject {
  alt_description?: string;
  urls: {
    regular: string;
    thumb: string;
  };
}

interface TransformedUnsplashObject {
  image_url: string;
  alt_description?: string;
}

const UnwrappedBusinessPreviewSlide = ({
  onStepChange,
  optimizely,
  userData,
}: PropsType) => {
  const { business_topic: businessTopic = '', plan_to_sell: planToSell } =
    userData;
  const [previewImageUrls, setPreviewImageUrls] = useState<
    TransformedUnsplashObject[]
  >([]);

  const [selected, setSelected] = useState('landing_page');
  const [cardProperties, setCardProperties] = useState([
    {
      id: LANDING_PAGE_ID,
      cardColor: Icon.CARD_COLORS.DANGER,
      cardIcon: Icon.ICONS.BLOG,
      cardText: 'Landing page',
    },
    {
      id: SALES_EMAIL_ID,
      cardColor: Icon.CARD_COLORS.LOCKED,
      cardIcon: Icon.ICONS.SEQUENCES,
      cardText: 'Sales email',
    },
    {
      id: CHECKOUT_ID,
      cardColor: Icon.CARD_COLORS.PUBLISHED,
      cardIcon: Icon.ICONS.ROUND_DOLLAR,
      cardText: 'Checkout',
    },
  ]);

  const breakpoints = useSageBreakpoint();

  const { fetchImagesByTopic } = useUnsplash();

  useEffect(() => {
    rudderstackTrackEvent(
      RUDDERSTACK_CONSTANTS.businessPreviewSlideName,
      RUDDERSTACK_CONSTANTS.slideViewed,
      optimizely?.user?.id
    );
  }, []);

  const handleOnComplete = () => {
    optimizely.track(OPTIMIZELY_CONSTANTS.aboutBusinessCompleted);
    rudderstackTrackEvent(
      RUDDERSTACK_CONSTANTS.businessPreviewSlideName,
      RUDDERSTACK_CONSTANTS.buttonClicked,
      optimizely?.user?.id
    );
    onStepChange();
    window.location.href = buildSignupUrl(userData);
  };

  const transformReturnedImages = (unsplashImages: UnsplashImageObject[]) => {
    return unsplashImages.map((obj, index) => {
      if (index < 9) {
        return {
          image_url: obj.urls.thumb,
          alt_description: obj.alt_description,
        };
      } else {
        return {
          image_url: obj.urls.regular,
          alt_description: obj.alt_description,
        };
      }
    });
  };

  // When this slide loads we will use the function exposed by the Unsplash service to fetch 10 images.
  // In order to populate all of the previews we need 9 thumbnails & 2 hero sized image.
  useEffect(() => {
    const fetchPreviewImages = async () => {
      const response = await fetchImagesByTopic(businessTopic, '11');
      if (response?.success) {
        const transformedImages = transformReturnedImages(response.data);
        setPreviewImageUrls(transformedImages);
      } else {
        console.error(response?.error);
      }
    };
    fetchPreviewImages();
  }, []);

  const handleClick = (component: string) => {
    setSelected(component);
  };

  const setUpCardProps = (planToSell: string) => {
    switch (planToSell) {
      case 'podcast':
        setCardProperties((previousCardProperties) =>
          uniqBy(
            [
              ...previousCardProperties,
              {
                id: PODCAST_ID,
                cardColor: Icon.CARD_COLORS.LOCKED,
                cardIcon: Icon.ICONS.MICROPHONE,
                cardText: 'Podcast',
              },
            ],
            'id'
          )
        );
        break;
      case 'community':
        setCardProperties((previousCardProperties) =>
          uniqBy(
            [
              ...previousCardProperties,
              {
                id: COMMUNITY_ID,
                cardColor: Icon.CARD_COLORS.WARNING,
                cardIcon: Icon.ICONS.CONVERSATION,
                cardText: 'Community',
              },
            ],
            'id'
          )
        );
        break;
      case 'coaching_program':
        setCardProperties((previousCardProperties) =>
          uniqBy(
            [
              ...previousCardProperties,
              {
                id: COACHING_PROGRAM_ID,
                cardColor: Icon.CARD_COLORS.DANGER,
                cardIcon: Icon.ICONS.PACKAGE,
                cardText: 'Coaching program',
              },
            ],
            'id'
          )
        );
        break;
      default:
        setCardProperties((previousCardProperties) =>
          uniqBy(
            [
              ...previousCardProperties,
              {
                id: COURSE_ID,
                cardColor: Icon.CARD_COLORS.INFO,
                cardIcon: Icon.ICONS.COURSE,
                cardText: 'Course',
              },
            ],
            'id'
          )
        );
    }
  };

  useEffect(() => {
    setUpCardProps(planToSell);
  }, [planToSell]);

  return (
    <Grid style={{ maxWidth: 'unset' }}>
      <Grid.Row style={{ justifyContent: 'center' }}>
        <Grid.Col>
          <Grid container={Grid.CONTAINER_SIZES.MD}>
            <Frame
              gap={Frame.GAPS.NONE}
              padding={Frame.PADDINGS[breakpoints.md ? 'MD' : 'LG']}
              width={Frame.WIDTHS.FLEX}
              style={{ paddingTop: breakpoints.md ? null : 80 }}
            >
              <Frame gap={Frame.GAPS.XS}>
                <Frame
                  gap={Frame.GAPS.XS}
                  direction={Frame.DIRECTIONS.HORIZONTAL}
                >
                  <Icon
                    icon={Icon.ICONS.CHECK_CIRCLE_FILLED}
                    size={Icon.SIZES.XXL}
                    color={Icon.COLORS.SAGE_300}
                  />
                  <h4
                    className={`
                      ${
                        SageClassnames.TYPE[
                          breakpoints.md ? 'HEADING_5' : 'HEADING_4'
                        ]
                      }
                      ${SageClassnames.TYPE_COLORS.CHARCOAL_500}
                    `}
                    style={{ fontWeight: 600, alignSelf: 'center' }}
                  >
                    Success!
                  </h4>
                </Frame>
                <Frame
                  gap={Frame.GAPS.XS}
                  className={
                    SageClassnames.SPACERS[
                      breakpoints.md ? 'NONE' : 'MD_BOTTOM'
                    ]
                  }
                >
                  <h3
                    className={`
                      ${
                        SageClassnames.TYPE[
                          breakpoints.md ? 'HEADING_4' : 'HEADING_3'
                        ]
                      }
                      ${SageClassnames.TYPE_COLORS.CHARCOAL_500}
                    `}
                  >
                    You’re ready to start selling!
                  </h3>
                  {breakpoints.md ? (
                    <></>
                  ) : (
                    <p
                      className={`
                      ${SageClassnames.TYPE.HEADING_5}
                      ${SageClassnames.TYPE_COLORS.CHARCOAL_300}
                    `}
                      style={{ fontWeight: 500, maxWidth: '452px' }}
                    >
                      {planToSell === 'course'
                        ? `Drive traffic to your landing page, collect leads and
                      automatically send a series of emails to drive purchases.`
                        : `Preview your Kajabi business: drive traffic, gather
                      leads, and launch email campaigns to promote your product.`}
                    </p>
                  )}
                </Frame>
              </Frame>
              {breakpoints.md ? (
                <Frame
                  direction={Frame.DIRECTIONS.HORIZONTAL}
                  gap={Frame.GAPS.XS}
                  style={{
                    display: 'inline-flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                  }}
                  className={SageClassnames.SPACERS.SM_TOP}
                >
                  {cardProperties.map((cardProps, key: number) => (
                    <React.Fragment
                      key={`business-preview-slide-mobile-${key}`}
                    >
                      <BusinessPreviewSlideTab
                        handleClick={handleClick}
                        id={cardProps.id}
                        icon={cardProps.cardIcon}
                        color={cardProps.cardColor}
                        text={cardProps.cardText}
                        selected={selected}
                      />
                    </React.Fragment>
                  ))}
                </Frame>
              ) : (
                <Frame>
                  {cardProperties.map((cardProps, i: number) => (
                    <React.Fragment key={`business-preview-slide-web-${i}`}>
                      <BusinessPreviewSlideTab
                        handleClick={handleClick}
                        id={cardProps.id}
                        icon={cardProps.cardIcon}
                        color={cardProps.cardColor}
                        text={cardProps.cardText}
                        selected={selected}
                      />
                      {/* removes the line from the bottom of the last card in the list */}
                      {i < 3 && <div className="vertical-line" />}
                    </React.Fragment>
                  ))}
                </Frame>
              )}
              {breakpoints.md ? (
                <></>
              ) : (
                <div
                  className={SageClassnames.SPACERS.LG_TOP}
                  style={{ width: breakpoints.md ? '100%' : '420px' }}
                >
                  <Button fullWidth={true} onClick={handleOnComplete}>
                    Save and continue
                  </Button>
                </div>
              )}
            </Frame>
          </Grid>
        </Grid.Col>
        <Grid.Col size={breakpoints.md ? 12 : 7}>
          <Grid container={Grid.CONTAINER_SIZES.LG}>
            <Frame
              gap={Frame.GAPS.NONE}
              padding={Frame.PADDINGS[breakpoints.md ? 'MD' : 'XXL']}
              width={Frame.WIDTHS.FLEX}
              style={{ paddingTop: breakpoints.md ? 0 : 80 }}
            >
              <Frame gap={Frame.GAPS[breakpoints.md ? 'MD' : 'LG']}>
                <Frame gap={Frame.GAPS.XS}>
                  <BusinessPreviewSlideContent
                    selected={selected}
                    userData={userData}
                    previewImageUrls={previewImageUrls}
                  />
                </Frame>
              </Frame>
            </Frame>
            {breakpoints.md ? (
              <div
                style={{
                  width: breakpoints.sm ? '95%' : '420px',
                  paddingLeft: '5%',
                  zIndex: 9999,
                  position: 'fixed',
                  bottom: '20px',
                }}
              >
                <Button fullWidth={true} onClick={handleOnComplete}>
                  Save and continue
                </Button>
              </div>
            ) : (
              <></>
            )}
          </Grid>
        </Grid.Col>
      </Grid.Row>
    </Grid>
  );
};

export const BusinessPreviewSlide = withOptimizely(
  UnwrappedBusinessPreviewSlide
);
